import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import styled from 'styled-components';
import Lottie from 'lottie-react';

import LoadingAnimation from '../../../Animation/NewLoading.json';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';
import FetchModule from '../../Share/Network/FetchModule';

import LockerAddModal from './LockerAddModal';
import LockerDefaultView from './LockerDefaultView';
import LockerChangeNameModal from './LockerChangeNameModal';
import LockerUsingStopModal from './LockerUsingStopModal';
import LockerStartModal from './LockerStartModal';
import LockerHistoryModal from './LockerHistoryModal';
import CustomAlert from '../../Share/Normal/CustomAlert';
import LockerExtendsModal from './LockerExtendsModal';
import LockerDivisionList from './LockerDivisionList';
import LockerUsageAddModal from '../../Individual/MemberModel/MemberDetail/Modal/LockerUsageAddModal';

import usePointerReorder from '../../Share/Normal/usePointerReorder';

function LockerView() {
  const history = useHistory();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [isClickedAdd, setIsClickedAdd] = useState(false);
  const [isClickedChangeName, setIsClickedChangeName] = useState(false);
  const [isClickedStart, setIsClickedStart] = useState(false);
  const [isClickedStop, setIsClickedStop] = useState(false);
  const [isClickedHistory, setIsClickedHistory] = useState(false);
  const [isClickedExtend, setIsClickedExtend] = useState(false);
  const [isClickedModifyLocker, setIsClickedModifyLocker] = useState(false);
  const [isViewCustomAlert, setIsViewCustomAlert] = useState(false);

  const [lockerDivisions, setLockerDivisions] = useState([]);
  const [lockerInfo, setLockerInfo] = useState(undefined);
  const [isClickedAddLockerDivision, setIsClickedAddLockerDivision] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [newLockerName, setNewLockerName] = useState('');
  const [isInProgressAddLocker, setIsInProgressAddLocker] = useState(false);
  const [isInProgressDeleteLocker, setIsInProgressDeleteLocker] = useState(false);
  const [isInProgressLockerDivision, setIsInProgressLockerDivision] = useState(false);
  const [isClickedLockerDivisionId, setIsClickedLockerDivisionId] = useState(0);
  const [totalLockers, setTotalLockers] = useState([]);
  const [selectedAddLockerDivision, setSelectedAddLockerDivision] = useState({ id: 0, name: t('main.locker.all') });

  const [text, setText] = useState('');
  const [lockerData, setLockerData] = useState([]);
  const [startModalLockerData, setStartModalLockerData] = useState(null);
  const [extendModalMemberData, setExtendModalMemberData] = useState(undefined);
  const [modifyModalMemberData, setModifyModalMemberData] = useState(undefined);
  const [modifyModalLockerData, setModifyModalLockerData] = useState(null);
  const [index, setIndex] = useState(-1);
  const [lockerIndex, setLockerIndex] = useState(-1);
  const [rendering, setRendering] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const [divisionNameDraft, setDivisionNameDraft] = useState({});

  function onChangeDivisionNameDraft(id, name) {
    setDivisionNameDraft(prev => ({ ...prev, [id]: name }));
  }

  function onClearDivisionNameDraft(id) {
    setDivisionNameDraft(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  // 구분 전환 시 draft 초기화 //
  useEffect(() => {
    setDivisionNameDraft({});
  }, [isClickedLockerDivisionId]);

  const AddModalRef = useRef();
  const NameModalRef = useRef();
  const StartModalRef = useRef();
  const LockerRef = useRef([]);

  // 포인터 기반 드래그 순서 변경 훅 (마우스/터치/펜 통합) //
  const reorder = usePointerReorder({ onDrop: onDropDivision });

  // 락커 데이터 불러오기 //
  useEffect(() => {
    setIsLoading(true);
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('locker/', 'GET');

        if (responseData.status === 200) {
          let divisions = responseData.data.lockerDivisions.slice();
          let lockers = responseData.data.lockers.slice();
          lockers.sort((a, b) => ((a.y ?? 0) * 10000 + (a.x ?? 0)) - ((b.y ?? 0) * 10000 + (b.x ?? 0)));
          let newTotalLockers = [];

          divisions.unshift({ id: 0, name: t('main.locker.all') });

          for (let i = 0; i < divisions.length; i++) {
            let newDivision = {
              id: divisions[i].id,
              name: divisions[i].name,
              lockers: []
            }

            newTotalLockers.push(newDivision);
          }
          
          newTotalLockers.splice(1, 0, { id: -1, name: t('main.locker.other'), lockers: [] });

          for (let i = 0; i < lockers.length; i++) {
            if (lockers[i].lockerDivisionId === null || lockers[i].lockerDivisionId === undefined || lockers[i].lockerDivisionId === 0) {
              newTotalLockers.find(d => d.id === -1).lockers.push(lockers[i]);
            }
            else {
              for (let j = 0; j < newTotalLockers.length; j++) {
                if (lockers[i].lockerDivisionId === newTotalLockers[j].id) {
                  newTotalLockers[j].lockers.push(lockers[i]);
                  break;
                }
              }
            }
          }

          setLockerDivisions(divisions);
          setTotalLockers(newTotalLockers);
          requestAnimationFrame(() => requestAnimationFrame(() => {
            setIsSaveLoading(false);
            setIsLoading(false);
          }));
        }
        else if (responseData.status === 401) {
          alert(t('main.locker.loginRequired'));
          history.push('/');
        }
      }
    )();
  }, [history, rendering])

  function onClickModalExit() {
    setSelectedAddLockerDivision({ id: 0, name: t('main.locker.all') });
    setIsClickedAdd(false);
  }

  function onClickAddModalSave() {
    let count = AddModalRef.current.getValue();

    if (count === 0) {
      alert(t('main.locker.enterLockerCount'));
      return;
    }
    else if (isInProgressAddLocker) {
      alert(t('main.locker.lockerSavingInProgress'));
      return;
    }

    setIsInProgressAddLocker(true);

    let requestData = {
      count: count,
      lockerDivisionId: selectedAddLockerDivision.id === 0 ? null : selectedAddLockerDivision.id
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('locker/save', 'POST', requestData);

        if (responseData.status === 200) {
          setRendering(!rendering);
          setIsInProgressAddLocker(false);
          setIsClickedAdd(false);
        }
        else if (responseData.status === 401) {
          alert(t('main.locker.loginRequired'));
          history.push('/');
          setIsInProgressAddLocker(false);
        }
        else {
          alert(t('main.locker.addLockerFailed'));
          setIsInProgressAddLocker(false);
        }
      }
    )();
  }

  function onClickSaveChangeNameModal(name) {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.patchData('locker/modify/name/'
          + totalLockers[index].lockers[lockerIndex].id
          + '/'
          + name, 'PATCH');

        if (responseData.status === 200) {
          setRendering(!rendering);
          setIsClickedChangeName(false);
          setIndex(-1);
          onClickMoreButton();
        }
        else if (responseData.status === 204) { }
        else if (responseData.status === 401) {
          alert(t('main.locker.loginRequired'));
          history.push('/');
        }
      }
    )();
  }

  function onClickMoreButton() {
    for (let i = 0; i < totalLockers.length; i++) {
      for (let j = 0; j < totalLockers[i].lockers.length; j++) {
        LockerRef.current[i]?.[j]?.setClickedMore?.(false);
      }
    }
  }

  function onClickUsageHistory(index, lockerIndex) {
    setIndex(index);
    setLockerIndex(lockerIndex);
    setIsClickedHistory(true);
  }

  function onClickChangeState(index, lockerIndex) {
    if (totalLockers[index].lockers[lockerIndex].state === 'Using') {
      setText(t('main.locker.endUsageFirst'));
      setIsViewCustomAlert(true);
      return;
    }
    else if (totalLockers[index].lockers[lockerIndex].state === 'Invalid') {
      setText(t('main.locker.alreadyLocked'));
      setIsViewCustomAlert(true);
      return;
    }

    let state = '';

    if (totalLockers[index].lockers[lockerIndex].state === 'Empty') state = 'Invalid';

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.patchData('locker/modify/'
          + totalLockers[index].lockers[lockerIndex].id
          + '/'
          + state, 'PATCH');

        if (responseData.status === 200) {
          setRendering(!rendering);
          onClickMoreButton();
        }
        else if (responseData.status === 204) { }
        else if (responseData.status === 401) {
          alert(t('main.locker.loginRequired'));
          history.push('/');
        }
      }
    )();
  }

  function onClickDeleteLocker(index, lockerIndex) {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.deleteData('locker/delete/'
          + totalLockers[index].lockers[lockerIndex].id, 'DELETE');

        if (responseData.status === 200) {
          setRendering(!rendering);
          onClickMoreButton();
        }
        else if (responseData.status === 205) {
          setText(t('main.locker.lockerInUse'));
          setIsViewCustomAlert(true);
        }
        else if (responseData.status === 401) {
          alert(t('main.locker.loginRequired'));
          history.push('/');
        }
      }
    )();
  }

  function onClickChangeName(index, lockerIndex, newName) {
    if (newName !== undefined) {
      const fetchModule = new FetchModule();
      (
        async () => {
          const responseData = await fetchModule.patchData('locker/modify/name/'
            + totalLockers[index].lockers[lockerIndex].id
            + '/'
            + newName, 'PATCH');

          if (responseData.status === 200) setRendering(!rendering);
          else if (responseData.status === 401) {
            alert(t('main.locker.loginRequired'));
            history.push('/');
          }
        }
      )();
      return;
    }

    setIndex(index);
    setLockerIndex(lockerIndex);
    setIsClickedChangeName(true);
  }

  function onClickLockerAction(index, lockerIndex) {
    setIndex(index);
    setLockerIndex(lockerIndex);

    const locker = totalLockers[index].lockers[lockerIndex];

    let state = '';

    if (locker.state === 'Invalid') {
      state = 'Empty';

      const fetchModule = new FetchModule();
      (
        async () => {
          const responseData = await fetchModule.patchData('locker/modify/'
            + locker.id
            + '/'
            + state, 'PATCH');

          if (responseData.status === 200) {
            setRendering(!rendering);
            onClickMoreButton();
          }
          else if (responseData.status === 204) { }
          else if (responseData.status === 401) {
            alert(t('main.locker.loginRequired'));
            history.push('/');
          }
        }
      )();
      return;
    }

    if (locker.state === 'Using') setIsClickedStop(true);
    else if (locker.state === 'Empty') {
      setStartModalLockerData(locker);
      setIsClickedStart(true);
    }
    else if (locker.state === 'Expected') {
      const fetchModule = new FetchModule();
      (async () => {
        const responseData = await fetchModule.getDataVersion2('locker/' + locker.memberId, 'GET');
        if (responseData.status === 200) {
          const matched = responseData.data.find(item => item.lockerId === locker.id && item.status === '결제 예정');
          setStartModalLockerData(matched ? { ...locker, ...matched, id: locker.id } : locker);
        }
        else {
          setStartModalLockerData(locker);
        }
        setIsClickedStart(true);
      })();
    }
  }

  function onClickSaveStopModal() {
    let requestData = {
      usageId: totalLockers[index].lockers[lockerIndex].usageId
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('locker/stop/', 'POST', requestData);

        if (responseData.status === 200) {
          alert(t('main.locker.usageEnded'));
          setRendering(!rendering);
          setIsClickedStop(false);
          setIndex(-1);
          onClickMoreButton();
        }
        else if (responseData.status === 401) {
          alert(t('main.locker.loginRequired'));
          history.push('/');
        }
        else {
          alert(t('main.locker.stopFailed'));
        }
      }
    )();
  }

  // 문자발송 버튼 클릭시
  function onClickSendMessageButton(index) {

    let text = lockerData[index].userName + '님, 지금까지 이용하셨던 락커 ' + lockerData[index].lockerName + '이(가) 만료되었어요';

    let requestData = {
      userId: lockerData[index].linkCoachId,
      data: text,
      location: 'Reservation',
      jsonData: ''
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataOfCoach('push_notification/send', 'POST', requestData);

        if (responseData.status === 200) {
          setText('푸시알림을 전송하였습니다!');
          setIsViewCustomAlert(true);
          LockerRef.current[index].setClickedMore(false);
        }
        else if (responseData.status === 204) {
          setText('Link Coach에 가입되어있지 않은 회원입니다!');
          setIsViewCustomAlert(true);
          LockerRef.current[index].setClickedMore(false);
        }
      }
    )();
  }

  // 사용 연장버튼 클릭시 //
  function onClickExtendsButton(index, lockerIndex) {
    setIndex(index);
    setLockerIndex(lockerIndex);

    const memberId = totalLockers[index].lockers[lockerIndex].memberId;
    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.postData('member/recently', 'POST', { memberId });
      setExtendModalMemberData(responseData.status === 200 ? responseData.data[0] : undefined);
      setIsClickedExtend(true);
    })();
  }

  function onClickLockerModify(index, lockerIndex) {
    setIndex(index);
    setLockerIndex(lockerIndex);

    const gridLocker = totalLockers[index].lockers[lockerIndex];
    const memberId = gridLocker.memberId;
    const fetchModule = new FetchModule();
    (async () => {
      const [recentlyRes, lockerRes] = await Promise.all([
        fetchModule.postData('member/recently', 'POST', { memberId }),
        fetchModule.getDataVersion2('locker/' + memberId, 'GET')
      ]);

      setModifyModalMemberData(recentlyRes.status === 200 ? recentlyRes.data[0] : undefined);

      if (lockerRes.status === 200) {
        const matched = lockerRes.data.find(item => item.lockerId === gridLocker.id && item.status === '사용중');
        setModifyModalLockerData(matched ?? null);
      }
      else {
        setModifyModalLockerData(null);
      }

      setIsClickedModifyLocker(true);
    })();
  }

  // 락커 추가 버튼 클릭시 //
  function onClickLockerDivisionAddButton() {
    if (isClickedAddLockerDivision) {
      if (newLockerName === '') {
        alert(t('main.locker.enterDivisionName'));
        return;
      }
      else if (isInProgressLockerDivision) {
        alert(t('main.locker.addingInProgress'));
        return;
      }

      setIsInProgressLockerDivision(true);

      let requestData = {
        name: newLockerName
      }

      const fetchModule = new FetchModule();
      (
        async () => {
          const responseData = await fetchModule.postDataVersion2('locker/division/save', 'POST', requestData);

          if (responseData.status === 200) {
            let divisions = lockerDivisions.slice();
            divisions.push(responseData.data);
            setLockerDivisions(divisions);

            let newTotalLockers = totalLockers.slice();
            newTotalLockers.push({
              id: responseData.data.id,
              name: responseData.data.name,
              lockers: []
            });
            setTotalLockers(newTotalLockers);

            setIsInProgressLockerDivision(false);
            setIsClickedAddLockerDivision(false);
            setIsClickedLockerDivisionId(responseData.data.id);
            setNewLockerName('');
          }
          else {
            setIsInProgressLockerDivision(false);
            setNewLockerName('');
          }
        }
      )();
    }
    else setIsClickedAddLockerDivision(true);
  }

  // 락커 구분 삭제 버튼 클릭시 //
  function onClickLockerDivisionDeleteButton() {
    if (isInProgressDeleteLocker) {
      alert(t('main.locker.divisionDeleteInProgress'));
      return;
    }

    setIsClickedAddLockerDivision(false);
    setIsInProgressDeleteLocker(true);

    const fetchModule = new FetchModule();
    (
      async () => {
        let divisionIdToDelete = isClickedLockerDivisionId;

        if (isClickedLockerDivisionId === -1) {
          const migrateRes = await fetchModule.postDataVersion2('locker/division/migrate-unassigned', 'POST', { name: t('main.locker.other') });
          if (migrateRes.status === 200) {
            divisionIdToDelete = migrateRes.data.id;
          }
          else if (migrateRes.status === 201) {
            alert(t('main.locker.divisionNoUnassignedLockers'));
            setIsInProgressDeleteLocker(false);
            return;
          }
          else if (migrateRes.status === 202) {
            alert(t('main.locker.divisionNameDuplicateOrEmpty'));
            setIsInProgressDeleteLocker(false);
            return;
          }
          else if (migrateRes.status === 401) {
            alert(t('main.locker.loginRequired'));
            history.push('/');
            return;
          }
          else {
            alert(t('main.locker.divisionServerError'));
            setIsInProgressDeleteLocker(false);
            return;
          }
        }

        const responseData = await fetchModule.postDataVersion2('locker/division/delete', 'POST', { lockerDivisionId: divisionIdToDelete });

        if (responseData.status === 200) {
          alert(t('main.locker.deleteComplete'));
          setRendering(!rendering);
          setIsClickedLockerDivisionId(0);
          setIsInProgressDeleteLocker(false);
        }
        else if (responseData.status === 201) {
          if (responseData.error === 'Not Found') {
            alert(t('main.locker.divisionNotFound'));
          }
          else {
            alert(t('main.locker.lockersExistInDivision'));
          }
          setIsInProgressDeleteLocker(false);
        }
        else {
          alert(t('main.locker.divisionDeleteFailed'));
          setIsInProgressDeleteLocker(false);
        }
      }
    )();
  }

  function onClickExitStartLockerModal() {
    setIsClickedStart(false);
    setRendering(!rendering);
    setIndex(-1);
  }

  function onClickSaveStartLockerModal() {
    setIsClickedStart(false);
    setRendering(!rendering);
    setIndex(-1);
  }

  function onClickDivisionDeleteIcon(e) {
    e.stopPropagation();
    onClickLockerDivisionDeleteButton();
  }

  function onCancelAddDivision() {
    setIsClickedAddLockerDivision(false);
    setNewLockerName('');
  }

  function onSaveStartLocker() {
    setIsSaveLoading(true);
    setIsLoading(true);
  }

  function onDeleteBatchLockers(divisionIndex, lockerIds) {
    const fetchModule = new FetchModule();
    (
      async () => {
        for (const id of lockerIds) {
          await fetchModule.deleteData('locker/delete/' + id, 'DELETE');
        }
        setTotalLockers(prev => {
          const next = [...prev];
          const idSet = new Set(lockerIds);
          next[divisionIndex] = {
            ...next[divisionIndex],
            lockers: next[divisionIndex].lockers.filter(l => !idSet.has(l.id))
          };
          return next;
        });
      }
    )();
  }

  function onSaveDivisionName(divisionId, newName) {
    const fetchModule = new FetchModule();
    (
      async () => {
        if (divisionId === -1) {
          const responseData = await fetchModule.postDataVersion2('locker/division/migrate-unassigned', 'POST', { name: newName });

          if (responseData.status === 200) {
            setRendering(prev => !prev);
          }
          else if (responseData.status === 201) {
            alert(t('main.locker.divisionNoUnassignedLockers'));
          }
          else if (responseData.status === 202) {
            alert(t('main.locker.divisionNameDuplicateOrEmpty'));
          }
          else if (responseData.status === 401) {
            alert(t('main.locker.loginRequired'));
            history.push('/');
          }
          else {
            alert(t('main.locker.divisionServerError'));
          }
        }
        else {
          const responseData = await fetchModule.postDataVersion2('locker/division/modify', 'POST', { lockerDivisionId: divisionId, name: newName });

          if (responseData.status === 200) {
            setRendering(prev => !prev);
          }
          else if (responseData.status === 201) {
            alert(t('main.locker.divisionNotFound'));
          }
          else if (responseData.status === 202) {
            alert(t('main.locker.divisionNameDuplicateOrEmpty'));
          }
          else if (responseData.status === 401) {
            alert(t('main.locker.loginRequired'));
            history.push('/');
          }
          else {
            alert(t('main.locker.divisionServerError'));
          }
        }
      }
    )();
  }

  function onSaveLockerChanges(index, lockChanges, nameChanges, newLockers = [], coordChanges = {}, deletedLockerIds = []) {
    const fetchModule = new FetchModule();
    (
      async () => {
        for (const id of deletedLockerIds) {
          const result = await fetchModule.deleteData('locker/delete/' + id, 'DELETE');
          if (result.status === 401) {
            alert(t('main.locker.loginRequired'));
            history.push('/');
            return;
          }
        }

        if (newLockers.length > 0) {
          // 백엔드는 {name, lockerDivisionId, x, y}만 받음 — isLocked 메타데이터는 분리 //
          const payloadLockers = newLockers.map(({ isLocked, ...rest }) => rest);
          const responseData = await fetchModule.postDataVersion2('locker/save/with-coordinate', 'POST', { lockers: payloadLockers });
          if (responseData.status === 401) {
            alert(t('main.locker.loginRequired'));
            history.push('/');
            return;
          }
          // 생성된 락커 중 isLocked=true 인 것을 (x, y, name)로 매칭해 Invalid 로 업데이트 //
          if (responseData.status === 200 && Array.isArray(responseData.data)) {
            const createdLockers = responseData.data;
            for (const newL of newLockers) {
              if (!newL.isLocked) continue;
              const found = createdLockers.find(l =>
                l.x === newL.x
                && l.y === newL.y
                && (l.name === newL.name || l.lockerName === newL.name)
              );
              if (!found) continue;
              const result = await fetchModule.patchData('locker/modify/' + found.id + '/Invalid', 'PATCH');
              if (result.status === 401) {
                alert(t('main.locker.loginRequired'));
                history.push('/');
                return;
              }
            }
          }
        }

        for (const [lockerIndex, newIsLocked] of Object.entries(lockChanges)) {
          const locker = totalLockers[index].lockers[Number(lockerIndex)];
          if (!locker) continue;
          let state = '';
          if (newIsLocked && locker.state === 'Empty') state = 'Invalid';
          else if (!newIsLocked && locker.state === 'Invalid') state = 'Empty';
          if (state) {
            const result = await fetchModule.patchData('locker/modify/' + locker.id + '/' + state, 'PATCH');
            if (result.status === 401) {
              alert(t('main.locker.loginRequired'));
              history.push('/');
              return;
            }
          }
        }

        for (const [lockerIndex, newName] of Object.entries(nameChanges)) {
          const locker = totalLockers[index].lockers[Number(lockerIndex)];
          if (!locker) continue;
          const result = await fetchModule.patchData('locker/modify/name/' + locker.id + '/' + newName, 'PATCH');
          if (result.status === 401) {
            alert(t('main.locker.loginRequired'));
            history.push('/');
            return;
          }
        }

        for (const [lockerId, { x, y }] of Object.entries(coordChanges)) {
          const result = await fetchModule.postDataVersion2('locker/coordinate/update', 'POST', { lockerId: Number(lockerId), x, y });
          if (result.status === 401) {
            alert(t('main.locker.loginRequired'));
            history.push('/');
            return;
          }
        }

        setRendering(prev => !prev);
      }
    )();
  }

  // 드롭 처리 (usePointerReorder 의 onDrop 콜백) //
  function onDropDivision(sourceIdx, targetIdx, side) {
    if (sourceIdx === null || sourceIdx === targetIdx) return;

    // 기타 카테고리 드롭: migrate-unassigned 후 order 변경 //
    if (sourceIdx === -1) {
      const fetchModule = new FetchModule();
      (
        async () => {
          const migrateRes = await fetchModule.postDataVersion2('locker/division/migrate-unassigned', 'POST', { name: t('main.locker.other') });
          if (migrateRes.status === 200) {
            const newDivisionId = migrateRes.data.id;
            const orderNum = side === 'right' ? targetIdx + 2 : targetIdx + 1;
            await fetchModule.postDataVersion2('locker/division/change/order', 'POST', { lockerDivisionId: newDivisionId, orderNum });
            setRendering(prev => !prev);
          }
          else if (migrateRes.status === 201) alert(t('main.locker.divisionNoUnassignedLockers'));
          else if (migrateRes.status === 202) alert(t('main.locker.divisionNameDuplicateOrEmpty'));
          else if (migrateRes.status === 401) {
            alert(t('main.locker.loginRequired'));
            history.push('/');
          }
          else alert(t('main.locker.divisionServerError'));
        }
      )();
      return;
    }

    const sourceArrIdx = sourceIdx + 1;
    const tgtArrIdx = targetIdx + 1;
    let insertAt = side === 'right' ? tgtArrIdx + 1 : tgtArrIdx;
    if (sourceArrIdx < insertAt) insertAt -= 1;

    if (insertAt === sourceArrIdx) return;

    const newDivisions = lockerDivisions.slice();
    const draggedDivision = newDivisions[sourceArrIdx];
    newDivisions.splice(sourceArrIdx, 1);
    newDivisions.splice(insertAt, 0, draggedDivision);
    setLockerDivisions(newDivisions);

    const sourceTotalIdx = sourceIdx + 2;
    let insertTotalAt = side === 'right' ? targetIdx + 3 : targetIdx + 2;
    if (sourceTotalIdx < insertTotalAt) insertTotalAt -= 1;

    const newTotalLockers = totalLockers.slice();
    const draggedTotal = newTotalLockers[sourceTotalIdx];
    newTotalLockers.splice(sourceTotalIdx, 1);
    newTotalLockers.splice(insertTotalAt, 0, draggedTotal);
    setTotalLockers(newTotalLockers);

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('locker/division/change/order', 'POST', {
          lockerDivisionId: draggedDivision.id,
          orderNum: insertAt
        });

        if (responseData.status === 200) { }
        else if (responseData.status === 201) {
          alert(t('main.locker.divisionNotFound'));
          setRendering(prev => !prev);
        }
        else if (responseData.status === 202) {
          alert(t('main.locker.divisionInvalidOrder'));
          setRendering(prev => !prev);
        }
        else if (responseData.status === 401) {
          alert(t('main.locker.loginRequired'));
          history.push('/');
        }
        else {
          alert(t('main.locker.divisionServerError'));
          setRendering(prev => !prev);
        }
      }
    )();
  }

  return (
    <TopLevelWrapper>
      {
        isViewCustomAlert &&
        <CustomAlert text={text} closeFn={() => setIsViewCustomAlert(false)} />
      }
      {
        isClickedAdd &&
        <LockerAddModal
          ref={AddModalRef}
          lockerDivisions={lockerDivisions}
          selectedAddLockerDivision={selectedAddLockerDivision}
          setSelectedAddLockerDivision={setSelectedAddLockerDivision}
          exitClick={onClickModalExit}
          saveClick={onClickAddModalSave} />
      }
      {
        isClickedChangeName &&
        <LockerChangeNameModal
          ref={NameModalRef}
          data={totalLockers}
          index={index}
          lockerIndex={lockerIndex}
          exitClick={() => setIsClickedChangeName(false)}
          saveClick={onClickSaveChangeNameModal} />
      }
      {
        isClickedStop &&
        <LockerUsingStopModal
          data={totalLockers[index].lockers[lockerIndex]}
          exitClick={() => setIsClickedStop(false)}
          saveClick={onClickSaveStopModal} />
      }
      {
        isClickedStart &&
        <LockerStartModal
          ref={StartModalRef}
          lockerData={startModalLockerData ?? totalLockers[index].lockers[lockerIndex]}
          exitClick={onClickExitStartLockerModal}
          saveClick={onClickSaveStartLockerModal} />
      }
      {
        isClickedHistory &&
        <LockerHistoryModal
          data={totalLockers[index].lockers[lockerIndex]}
          exitClick={() => setIsClickedHistory(false)} />
      }
      {
        isClickedExtend &&
        <LockerExtendsModal
          memberData={{ name: totalLockers[index].lockers[lockerIndex].userName }}
          recentlyMembership={extendModalMemberData}
          lockerData={totalLockers[index].lockers[lockerIndex]}
          lockerRendering={() => setRendering(!rendering)}
          closeFn={() => setIsClickedExtend(false)} />
      }
      {
        isClickedModifyLocker &&
        <LockerUsageAddModal
          memberData={{ name: totalLockers[index].lockers[lockerIndex].userName }}
          recentlyMembership={modifyModalMemberData}
          clickLockerData={{
            cardPrice: 0,
            cashPrice: 0,
            point: 0,
            ...totalLockers[index].lockers[lockerIndex],
            ...(modifyModalLockerData ?? {}),
            lockerId: Number(totalLockers[index].lockers[lockerIndex].id),
            usageId: Number(totalLockers[index].lockers[lockerIndex].usageId)
          }}
          memberId={Number(totalLockers[index].lockers[lockerIndex].memberId)}
          totalPoint={0}
          lockerRendering={() => setRendering(!rendering)}
          onClickExit={() => {
            setIsClickedModifyLocker(false);
            setModifyModalLockerData(null);
          }} />
      }
      <TopComponentWrapper>
        <TitleWrapper>
          <Title>{t('main.locker.title')}</Title>
          <Explanation>{t('main.locker.pageDesc')}</Explanation>
        </TitleWrapper>
      </TopComponentWrapper>
      <LockerDivisionButtonWrapper>
        <LockerDivisionList
          reorder={reorder}
          isClickedLockerDivisionId={isClickedLockerDivisionId}
          setIsClickedLockerDivisionId={setIsClickedLockerDivisionId}
          totalLockers={totalLockers}
          lockerDivisions={lockerDivisions}
          divisionNameDraft={divisionNameDraft}
          onClickDivisionDeleteIcon={onClickDivisionDeleteIcon}
          isClickedAddLockerDivision={isClickedAddLockerDivision}
          newLockerName={newLockerName}
          setNewLockerName={setNewLockerName}
          onCancelAddDivision={onCancelAddDivision}
          onClickLockerDivisionAddButton={onClickLockerDivisionAddButton}
          isEditMode={isEditMode} />
      </LockerDivisionButtonWrapper>
      <TotalViewBoxWrapper>
        <ViewBox>
          {isLoading && !isSaveLoading && (
            <LoadingOverlay>
              <LottieWrapper>
                <Lottie animationData={LoadingAnimation} loop />
              </LottieWrapper>
            </LoadingOverlay>
          )}
          <LockerDefaultView
            totalLockers={totalLockers}
            isClickedLockerDivisionId={isClickedLockerDivisionId}
            LockerRef={LockerRef}
            isSaveLoading={isSaveLoading}
            onSaveStart={onSaveStartLocker}
            onSaveLockerChangesFn={onSaveLockerChanges}
            onSaveDivisionNameFn={onSaveDivisionName}
            onClickUsageHistoryFn={onClickUsageHistory}
            onClickLockerActionFn={onClickLockerAction}
            onClickChangeStateFn={onClickChangeState}
            onClickDeleteLockerFn={onClickDeleteLocker}
            onClickChangeNameFn={onClickChangeName}
            onClickSendMessageFn={onClickSendMessageButton}
            onClickExtendFn={onClickExtendsButton}
            onClickModifyFn={onClickLockerModify}
            onDeleteLockersFn={onDeleteBatchLockers}
            onEditModeChange={setIsEditMode}
            onChangeDivisionNameDraft={onChangeDivisionNameDraft}
            onClearDivisionNameDraft={onClearDivisionNameDraft} />
        </ViewBox>
      </TotalViewBoxWrapper>
    </TopLevelWrapper>
  )
}

export default LockerView;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  margin-left: 10px;

  width: 1035px;
`;

const TopComponentWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: space-between;

  width: 100%;
`;


const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  width: 100%;

  gap: 4px;
`;

const Title = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const Explanation = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 12px;

  color: ${props => props.theme.subText};

  margin: 0 0 0 0;

  white-space: nowrap;
`;

const AddButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  min-width: 100px;
  height: 38px;

  border: 1px solid ${props => props.theme.buttonStroke};
  border-radius: 10px;
  background-color: ${props => props.theme.primary};

  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.subKeyHover};
  }
`;

const LockerDivisionButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: flex-start;

  margin-top: 30px;

  width: 100%;
`;

const TotalViewBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 1014px;

  margin-top: 20px;
  margin-bottom: 96px;
`;

const ViewBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  width: 100%;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  margin: 200px 0;

  border-radius: inherit;
  z-index: 10;
`;

const LottieWrapper = styled.div`
  width: 130px;
  height: 130px;
`;


